import React, { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styles from './CampaignCreditAssignment.module.css';

const CampaignCreditAssignment = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const token = localStorage.getItem('token');
  const creatorId = localStorage.getItem('userId');

  const [formData, setFormData] = useState({
    campaignId: '',
    amount: '',
    notes: ''
  });

  useEffect(() => {
    if (token) {
      fetchCampaigns();
    } else {
      setError('No authentication token found. Please login first.');
      setLoading(false);
    }
  }, [token]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`https://prod.toxboxai.com/api/election-campaign/by-creator/${creatorId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.');
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      let campaignArray = [];
      if (Array.isArray(data)) {
        campaignArray = data;
      } else if (data && typeof data === 'object') {
        campaignArray = Array.isArray(data.campaigns)
          ? data.campaigns
          : Array.isArray(data.data)
            ? data.data
            : Array.isArray(data.results)
              ? data.results
              : [data];
      }

      setCampaigns(campaignArray);
      console.log('Fetched campaigns:', campaignArray);

    } catch (err) {
      console.error('Error fetching campaigns:', err);
      setError(err.message || 'Failed to fetch campaigns. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setError('No authentication token found. Please login first.');
      return;
    }

    if (!formData.campaignId) {
      setError('Please select a campaign');
      return;
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const response = await fetch('https://prod.toxboxai.com/api/campaign/assigncredit', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          campaignId: formData.campaignId,
          amount: parseFloat(formData.amount),
          notes: formData.notes
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please login again.');
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      window.dispatchEvent(new Event('balanceChanged'));
      console.log('Credit assignment successful:', result);

      toast.success('Credit assigned successfully!', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: 'colored'
      });

      setFormData({
        campaignId: '',
        amount: '',
        notes: ''
      });

      fetchCampaigns();

    } catch (err) {
      console.error('Error assigning credit:', err);
      setError(err.message || 'Failed to assign credit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <div className={styles.cardBody}>
            <div className={`${styles.alert} ${styles.alertError}`}>
              <h3>Authentication Required</h3>
              <p>Please login to access the campaign credit assignment feature.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <ToastContainer />
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2>Assign Campaign Credit</h2>
        </div>

        <div className={styles.cardBody}>
          {error && (
            <div className={`${styles.alert} ${styles.alertError}`}>
              {error}
            </div>
          )}

          {loading ? (
            <div className={styles.loading}>
              <div className={styles.spinner}></div>
              <p>Loading campaigns...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className={styles.creditForm}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="campaignId">Select Campaign *</label>
                  <select
                    id="campaignId"
                    name="campaignId"
                    value={formData.campaignId}
                    onChange={handleInputChange}
                    required
                    className={styles.formSelect}
                    disabled={loading || submitting}
                  >
                    <option value="">Choose a campaign...</option>
                    {Array.isArray(campaigns) && campaigns.map((campaign) => (
                      <option key={campaign._id || campaign.id} value={campaign._id || campaign.id}>
                        {`${campaign.leaderName} - ${campaign.partyName} - ${campaign.Constituency}` || `Campaign ${campaign._id || campaign.id}`}
                      </option>
                    ))}
                  </select>
                  {Array.isArray(campaigns) && campaigns.length === 0 && !loading && (
                    <small className={styles.formHelp}>No campaigns found. Please create a campaign first.</small>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="amount">Credit Amount *</label>
                  <input
                    type="number"
                    id="amount"
                    name="amount"
                    value={formData.amount}
                    onChange={handleInputChange}
                    placeholder="0.00"
                    min="0.01"
                    step="0.01"
                    required
                    className={styles.formInput}
                    disabled={submitting}
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="notes">Notes (Optional)</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Add any notes about this credit assignment..."
                  rows="3"
                  className={styles.formTextarea}
                  disabled={submitting}
                />
              </div>

              <div className={styles.formActions}>
                <button
                  type="submit"
                  disabled={submitting || loading || !Array.isArray(campaigns) || campaigns.length === 0}
                  className={`${styles.btn} ${styles.btnPrimary}`}
                >
                  {submitting ? (
                    <>
                      <span className={styles.spinnerSmall}></span>
                      Assigning...
                    </>
                  ) : (
                    'Assign Credit'
                  )}
                </button>

                <button
                  type="button"
                  onClick={fetchCampaigns}
                  disabled={loading || submitting}
                  className={`${styles.btn} ${styles.btnSecondary}`}
                >
                  {loading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignCreditAssignment;